import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import SEO, { breadcrumbSchema } from '@/components/SEO';
import CTASection from '@/components/CTASection';
import { portfolioCategories } from '@/data/portfolio-data';
import MasonryGallery from '@/components/portfolio/MasonryGallery';

const PortfolioCategory = () => {
  const { category } = useParams<{ category: string }>();
  const cat = portfolioCategories.find((c) => c.slug === category);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);

  if (!cat) return <Navigate to="/portfolio" replace />;

  return (
    <>
      <SEO
        title={`${cat.name} — Portfolio | Cygnature Production`}
        description={`${cat.tagline} Découvrez nos réalisations ${cat.name.toLowerCase()} en images.`}
        path={`/portfolio/${category}`}
        jsonLd={[breadcrumbSchema([
          { name: 'Accueil', path: '/' },
          { name: 'Portfolio', path: '/portfolio' },
          { name: cat.name, path: `/portfolio/${category}` },
        ])]}
      />

      {/* Hero */}
      <section className="relative px-6 lg:px-8 py-24 md:py-32 bg-petrol text-petrol-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-petrol via-petrol/95 to-petrol" />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-1.5 text-xs text-primary font-heading font-medium mb-6 hover:gap-2.5 transition-all"
          >
            <ArrowLeft size={12} /> Retour au portfolio
          </Link>
          <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
            <cat.icon size={26} className="text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold">{cat.name}</h1>
          <p className="mt-3 font-heading text-sm italic text-primary/80">{cat.tagline}</p>
          <p className="mt-2 text-sm text-petrol-foreground/50">
            {cat.images.length} photo{cat.images.length > 1 ? 's' : ''}
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <MasonryGallery images={cat.images} pageSize={24} />
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold">
            Votre projet mérite la même attention
          </h2>
          <p className="mt-3 font-heading text-sm italic text-petrol-foreground/60">
            Disponible à Troyes, dans l'Aube et partout en France.
          </p>
          <Link
            to="/contact"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 mt-8 bg-primary text-primary-foreground rounded-full px-8 py-3.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Parlons de votre projet <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default PortfolioCategory;
